import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { CheckCircle2, ArrowRight, Scale, GraduationCap, ShieldCheck } from "lucide-react";

const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_bridge-legal-2/artifacts/ha8szgk5_Screenshot%202026-05-09%20111158.png";
const SITE_URL = "https://instabridgefunding.com";

export default function About() {
  useSEO(
    "About · Built by a lawyer, run like it",
    "Instabridge Funding was founded by Adam Gurney, a practicing attorney, after seeing how broken the legal-funding industry had become. Settlement-first underwriting, fair terms, attorney-founded.",
    {
      canonical: `${SITE_URL}/about`,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            name: "Instabridge Funding",
            url: SITE_URL,
            logo: `${SITE_URL}/favicon.svg`,
            description: "Attorney-founded pre-settlement legal funding. Non-recourse cash advances against future settlements for injured plaintiffs.",
            foundingDate: "2023",
            founder: {
              "@type": "Person",
              name: "Adam Gurney",
              jobTitle: "Founder & Practicing Attorney",
              image: FOUNDER_IMG,
              worksFor: { "@type": "Organization", name: "Instabridge Funding" },
            },
          },
          {
            "@type": "Person",
            name: "Adam Gurney",
            jobTitle: "Founder, Instabridge Funding · Practicing Attorney",
            image: FOUNDER_IMG,
            url: `${SITE_URL}/about`,
          },
          {
            "@type": "AboutPage",
            name: "About Instabridge Funding",
            url: `${SITE_URL}/about`,
            mainEntity: { "@type": "Organization", name: "Instabridge Funding" },
          },
        ],
      },
    }
  );

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />

      <section className="bg-[#F9FAFB] border-b border-[#E5E7EB] py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">About Instabridge</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05] max-w-3xl">
            Built by a lawyer.<br /><span className="font-semibold">Run like it.</span>
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] bg-[#0A2540] rounded-2xl overflow-hidden relative">
              <img src={FOUNDER_IMG} alt="Adam Gurney, founder of Instabridge Funding" className="absolute inset-0 w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="font-display text-2xl font-semibold">Adam Gurney</div>
                <div className="text-sm text-white/80">Founder · Practicing attorney</div>
              </div>
            </div>
            <p className="text-xs text-[#9CA3AF] italic mt-3">Adam Gurney, founder of Instabridge Funding and practicing attorney.</p>
          </div>
          <div className="lg:col-span-7">
            <div className="uppercase-label mb-4">The founder's story</div>
            <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light leading-tight">
              Instabridge was built by someone who understands the case, the client, and <span className="font-semibold">the pressure on the firm.</span>
            </h2>
            <div className="mt-6 text-[#374151] leading-relaxed space-y-4">
              <p>Adam Gurney is a practicing attorney — law school, courtrooms, cases that matter.</p>
              <p>
                He started Instabridge after a conversation with a peer exposed how broken the funding industry really was. Companies built by finance people who saw attorneys as lead sources and plaintiffs as collateral. No white-glove service. No real understanding of the cases. Just volume.
              </p>
              <p>
                He knew it could be better — because he'd already built a practice on the same principles: transparency, fair terms, and treating people like professionals.
              </p>
              <p className="text-[#0A2540] font-medium font-display text-lg leading-relaxed">
                Instabridge is what happens when a lawyer builds the funding company instead of just endorsing one.
              </p>
            </div>
            <div className="flex gap-3 mt-9">
              <Link to="/contact"><Button className="btn-primary-navy rounded-full h-11 px-6">Schedule a call</Button></Link>
              <Link to="/for-attorneys"><Button variant="outline" className="rounded-full h-11 px-6">For attorneys →</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">What we stand for</div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight max-w-3xl">Our principles.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            <Principle t="Protect the settlement" d="We cap advances. Always. Overfunding is bad for plaintiffs, bad for attorneys, and bad for the long-term industry." />
            <Principle t="Treat people like professionals" d="Attorneys aren't leads. Plaintiffs aren't collateral. Every interaction reflects that." />
            <Principle t="Transparency without exception" d="Every fee, every rate, every term — disclosed in plain English before a dollar moves. No surprises, ever." />
          </div>
        </div>
      </section>

      {/* Attorney-founded difference — EEAT reinforcement */}
      <section className="py-20 bg-white border-t border-[#E5E7EB]">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="uppercase-label mb-3">Why attorney-founded matters</div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight max-w-3xl">The difference an attorney at the top makes.</h2>
          <p className="mt-5 text-[#374151] leading-relaxed max-w-3xl">
            Most pre-settlement funding companies are run by finance professionals who learned about lawsuits from a spreadsheet. Instabridge is different because the person underwriting your case has actually stood in a courtroom, negotiated a settlement, and satisfied a lien from a trust account. That shows up in three places.
          </p>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-6" data-testid="about-eeat-underwriting">
              <Scale className="w-5 h-5 text-[#0066FF]" />
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">Case-first underwriting</h3>
              <p className="text-sm text-[#374151] mt-3 leading-relaxed">We evaluate liability, damages, and timeline the way plaintiff attorneys do — because that's how the person setting our underwriting rules thinks. Not credit models. Not scoring algorithms. Case merits.</p>
              <Link to="/how-it-works" className="mt-4 inline-block text-sm text-[#0066FF] hover:underline font-medium">How we underwrite →</Link>
            </div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-6" data-testid="about-eeat-cap">
              <ShieldCheck className="w-5 h-5 text-[#0066FF]" />
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">Settlement-protective caps</h3>
              <p className="text-sm text-[#374151] mt-3 leading-relaxed">We cap advances at 8–15% of the conservative projected settlement. A lawyer running the company doesn't want plaintiffs walking away from settlement with nothing — even if it means smaller advances than a competitor would offer.</p>
              <Link to="/services/pre-settlement" className="mt-4 inline-block text-sm text-[#0066FF] hover:underline font-medium">See the cap in action →</Link>
            </div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-6" data-testid="about-eeat-transparency">
              <GraduationCap className="w-5 h-5 text-[#0066FF]" />
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">Contracts a lawyer wrote</h3>
              <p className="text-sm text-[#374151] mt-3 leading-relaxed">Every Instabridge contract discloses every fee, every rate, every accrual tier from 6 months to 4+ years — up front, in plain language. Because a lawyer signed off on the wording before it went to a single plaintiff.</p>
              <Link to="/tools/funding-cost-calculator" className="mt-4 inline-block text-sm text-[#0066FF] hover:underline font-medium">See the full cost schedule →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Giving back — visually elevated card to make community/scholarship work stand out */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#0A2540]/90 text-white p-8 lg:p-12" data-testid="about-scholarship-card">
            <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#0066FF]/25 blur-3xl pointer-events-none" />
            <div className="absolute -left-24 -bottom-24 w-72 h-72 rounded-full bg-[#0066FF]/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] uppercase tracking-widest font-semibold mb-5">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#6BA8FF]" /> Empowering futures
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tight">
                One case at a time. <span className="font-semibold">One scholarship at a time.</span>
              </h2>
              <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-3xl">
                When you choose Instabridge, you're not just securing the financial support you need — you're also making a difference. A portion of our proceeds goes directly to funding scholarships for aspiring law students from low-income backgrounds. Together, we're building a stronger, more diverse legal community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A2540] text-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold">Let's talk.</h2>
          <p className="mt-3 text-white/70">Whether you're a plaintiff, an attorney, or just curious about what we do — we'd love to hear from you.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/apply"><Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-12 px-7">Apply for funding</Button></Link>
            <Link to="/contact"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0A2540] rounded-full h-12 px-7">Contact us <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Principle = ({ t, d }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-7">
    <CheckCircle2 className="w-5 h-5 text-[#0066FF]" />
    <h3 className="font-display text-xl font-semibold text-[#0A2540] mt-4">{t}</h3>
    <p className="text-[#6B7280] mt-3 leading-relaxed">{d}</p>
  </div>
);
